/**
 * Página /vecino/mis-datos — Sprint 13 S13-32 (rediseño editable).
 *
 * Antes (S11-V2-08): read-only con datos enmascarados.
 * Ahora: 4 secciones editables que cubren toda la info del vecino
 * sobre su casa:
 *
 * 1. Datos personales: nombre + RUT (read-only) + email + celular
 *    (editables vía PATCH /me/persona).
 * 2. Residentes: lista de quienes habitan la casa con estado
 *    (pendiente / aprobado / rechazado / inactivo). Agregar
 *    via POST /me/residentes (crea persona+residente atómicamente
 *    en pendiente_validacion).
 * 3. Vehículos: lista + agregar/editar/eliminar via
 *    POST/PATCH/DELETE de vehículos.
 * 4. Recursos asignados: read-only (llaveros, controles, citofonía,
 *    Wi-Fi). La asignación la maneja la Comisión.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useAuth } from '../../lib/auth/AuthContext';
import { ModalConfirmar } from '../../components/ui/ModalConfirmar';
import { ApiError } from '../../lib/api/client';
import { autoeditarPersona } from '../../lib/api/me';
import { formatRut, validarRutChileno } from '../../lib/format';
import { listarRecursosDeLote, listarVehiculosDeLote } from '../../lib/api/lotes';
import {
  actualizarResidenteConPersona,
  cancelarDesignacionAutorizado,
  desdesignarAutorizado,
  designarAutorizado,
  eliminarResidente,
  inactivarResidente,
  listarResidentesDeLote,
  type ParentescoLiteral,
  type ResidenteConPersonaRead,
} from '../../lib/api/residentes';
import { darDeBajaVehiculo } from '../../lib/api/vehiculos';
import type {
  AccesoCitofoniaRead,
  AccesoWifiRead,
  AuthMeResponse,
  EstadoVehiculo,
  RecursoConSuspensionRead,
  VehiculoRead,
} from '../../lib/api/types';
import { MAX_WIFI_POR_LOTE } from '../../lib/api/types';
import {
  darDeBajaCitofonia,
  darDeBajaWifi,
  listarCitofoniaDeLote,
  listarWifiDeLote,
  solicitarCitofonia,
  solicitarWifi,
} from '../../lib/api/accesos';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { InputCelular } from '../../components/ui/InputCelular';
import { InputFecha } from '../../components/ui/InputFecha';
import { InputRut, type RutValidez } from '../../components/ui/InputRut';
import { Modal } from '../../components/ui/Modal';
import {
  controlesEnBolsa,
  llaverosEnBolsa,
} from '../../components/vecino/AsignarRecursoBolsa';
import { MisRecursos } from '../../components/vecino/MisRecursos';
import { Patente } from '../../components/ui/Patente';
import { Pill } from '../../components/ui/Pill';
import {
  EstadoWifiInline,
  type EstadoWifi,
} from '../../components/ui/CirculoWifi';
import { EmptyState } from '../../components/ui/EmptyState';
import { AddCard } from '../../components/ui/AddCard';
import { Section, SectionHead } from '../../components/ui/Section';
import { InfoNote } from '../../components/ui/InfoNote';
import { ModalSolicitarRecurso } from '../../components/vecino/ModalSolicitarRecurso';
import { ModalReportarProblema } from '../../components/vecino/ModalReportarProblema';
import { ModalDejeBarrio } from '../../components/vecino/ModalDejeBarrio';
import { ModalAgregarResidente } from '../../components/vecino/ModalAgregarResidente';
import { ModalVehiculo } from '../../components/vecino/ModalVehiculo';
import {
  descargarMisDatos,
  solicitarEliminacion,
} from '../../lib/api/ley21719';
import {
  getResumenArrendado,
  type ResumenArrendado,
} from '../../lib/api/tenencias';
import { hoy } from '../../lib/tiempo';
import { PARENTESCO_LABEL, RECURSO_TIPO_LABEL } from '../../lib/labels';


function capitalizar(texto: string): string {
  if (!texto) return texto;
  return texto.charAt(0).toUpperCase() + texto.slice(1).toLowerCase();
}

// Sprint 14 S14-09: las funciones `badgeEstadoVehiculo` y
// `badgeEstadoResidente` previas (S13-32/33) fueron reemplazadas por
// `pillVarianteEstadoVehiculo` y `pillVarianteEstadoResidente` que
// retornan la variant de `<Pill>` del design system. Ver subcomponentes.

// ============================================================
// Modal editar residente (S15-Vec.3)
// ============================================================

interface ModalEditarResidenteProps {
  residente: ResidenteConPersonaRead;
  onCerrar: () => void;
  onGuardado: () => void;
}

function ModalEditarResidente({
  residente,
  onCerrar,
  onGuardado,
}: ModalEditarResidenteProps) {
  const exigeJustificacion = residente.estado === 'aprobado';
  // Estado rechazado: el vecino corrige en respuesta al motivo. Puede
  // adjuntar una aclaración OPCIONAL para la Comisión (ej. distinguir quién
  // es una segunda persona de "personal externo o de servicio"). Al guardar
  // vuelve a pendiente_validacion (el backend cierra el ciclo de rechazo).
  const esRechazado = residente.estado === 'rechazado';
  const [nombre, setNombre] = useState(residente.persona_nombre_completo);
  const [rut, setRut] = useState(residente.persona_rut ?? '');
  const [email, setEmail] = useState(residente.persona_email ?? '');
  // Celular y notas no vienen en ResidenteConPersonaRead; el usuario los
  // puede escribir desde cero si quiere actualizarlos. Si los deja
  // vacíos, el PATCH no toca esos campos (exclude_unset).
  const [celular, setCelular] = useState('');
  const [tocarCelular, setTocarCelular] = useState(false);
  const [parentesco, setParentesco] = useState<ParentescoLiteral>(
    residente.parentesco,
  );
  const [desde, setDesde] = useState(residente.desde);
  const [notas, setNotas] = useState(residente.notas ?? '');
  const [justificacion, setJustificacion] = useState('');
  // F3/H17: validez del RUT vía InputRut. Inicializa desde el RUT actual
  // (puede ser inválido si quedó data vieja — el vecino debe corregirlo).
  const [rutValidez, setRutValidez] = useState<RutValidez>(() =>
    validarRutChileno(residente.persona_rut ?? ''),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // F4: el RUT es obligatorio y debe ser válido (mod11) para guardar — esto
  // además fuerza a corregir un RUT viejo inválido en vez de re-enviarlo.
  //
  // Se lleva la LISTA de lo que falta, no un booleano: un botón gris sin decir
  // por qué obliga a cazar el campo entre cuatro. Caso real del 2026-09-04: la
  // titular de la 6986 no podía guardar y nadie —ella ni yo— dio con que era
  // la justificación hasta preguntárselo.
  const faltantes: string[] = [];
  if (nombre.trim().length === 0) faltantes.push('el nombre');
  if (!rutValidez.valid) faltantes.push('un RUT válido');
  if (desde === '') faltantes.push('la fecha "desde"');
  if (exigeJustificacion && justificacion.trim().length === 0) {
    faltantes.push('la justificación del cambio');
  }
  const valido = faltantes.length === 0;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valido || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload: Parameters<typeof actualizarResidenteConPersona>[1] = {
        nombre_completo: nombre.trim(),
        rut: rutValidez.canonico,
        email: email.trim() || null,
        parentesco,
        desde,
        notas: notas.trim() || null,
      };
      if (tocarCelular) {
        payload.celular = celular.trim() || null;
      }
      if (exigeJustificacion) {
        payload.justificacion_cambio = justificacion.trim();
      } else if (esRechazado && justificacion.trim()) {
        // Opcional en rechazado: solo se envía si el vecino la escribió.
        payload.justificacion_cambio = justificacion.trim();
      }
      await actualizarResidenteConPersona(residente.id, payload);
      onGuardado();
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Editar residente"
      onClose={onCerrar}
      footer={
        <>
          {/* Por qué el botón está gris. Sin esto hay que cazar el campo que
              falta entre cuatro, y el que se olvida más es la justificación
              —que solo aparece al editar un residente ya aprobado—. */}
          {faltantes.length > 0 && (
            <span className="text-xs text-text-muted mr-auto">
              Falta {faltantes.join(', ')}.
            </span>
          )}
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="form-editar-residente"
            disabled={!valido || submitting}
          >
            {submitting ? 'Guardando…' : 'Guardar'}
          </Button>
        </>
      }
    >
      <form
        id="form-editar-residente"
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Nombre completo <span className="text-state-danger">*</span>
          </span>
          <input
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            maxLength={200}
            className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
            required
          />
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">
              RUT <span className="text-state-danger">*</span>
            </span>
            <InputRut
              value={rut}
              onChange={(f, v) => {
                setRut(f);
                setRutValidez(v);
              }}
              aria-label="RUT del residente"
            />
          </label>
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">
              Parentesco <span className="text-state-danger">*</span>
            </span>
            <select
              value={parentesco}
              onChange={(e) =>
                setParentesco(e.target.value as ParentescoLiteral)
              }
              className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
            >
              {(Object.keys(PARENTESCO_LABEL) as ParentescoLiteral[]).map(
                (p) => (
                  <option key={p} value={p}>
                    {PARENTESCO_LABEL[p]}
                  </option>
                ),
              )}
            </select>
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">
              Email <span className="text-text-muted">(opcional)</span>
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={255}
              className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
            />
          </label>
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">
              Celular{' '}
              <span className="text-text-muted">
                {tocarCelular ? '(editando)' : '(no se modifica)'}
              </span>
            </span>
            <InputCelular
              value={celular}
              onChange={(v) => {
                setCelular(v);
                if (!tocarCelular) setTocarCelular(true);
              }}
            />
          </label>
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Vive aquí desde <span className="text-state-danger">*</span>
          </span>
          <InputFecha
            value={desde}
            onChange={setDesde}
            className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
            requerido
          />
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Notas <span className="text-text-muted">(opcional)</span>
          </span>
          <textarea
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            rows={2}
            className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
          />
        </label>

        {(exigeJustificacion || esRechazado) && (
          <label className="text-sm space-y-1 block">
            <span className="text-text-secondary">
              {exigeJustificacion ? (
                <>
                  Justificación del cambio{' '}
                  <span className="text-state-danger">*</span>
                </>
              ) : (
                <>
                  Aclaración para la Comisión{' '}
                  <span className="text-text-muted">(opcional)</span>
                </>
              )}
            </span>
            <textarea
              value={justificacion}
              onChange={(e) => setJustificacion(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder={
                exigeJustificacion
                  ? 'Ej: Corrección de RUT, cambio de celular, actualización de parentesco.'
                  : 'Si la Comisión pidió una aclaración en el motivo, escríbela aquí.'
              }
              className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
              required={exigeJustificacion}
            />
            <span className="text-xs text-text-muted block">
              Al guardar, el residente volverá a estar pendiente de revisión por
              la Comisión.
            </span>
          </label>
        )}

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

// ============================================================
// Subcomponentes de cards (Sprint 14 S14-09)
// ============================================================

function pillVarianteEstadoResidente(estado: ResidenteConPersonaRead['estado']) {
  switch (estado) {
    case 'aprobado':
      return 'success' as const;
    case 'pendiente_validacion':
      return 'pending' as const;
    case 'rechazado':
      return 'danger' as const;
    case 'inactivo':
    default:
      return 'info' as const;
  }
}

function labelEstadoResidente(estado: ResidenteConPersonaRead['estado']) {
  // Sprint 18 P11: vocabulario validación. El enum del backend sigue
  // siendo aprobado/rechazado/pendiente_validacion; aquí solo cambia
  // lo que ve el vecino.
  switch (estado) {
    case 'aprobado':
      return 'Validado';
    case 'pendiente_validacion':
      return 'En revisión';
    case 'rechazado':
      return 'No validado';
    case 'inactivo':
      return 'Salido';
    default:
      return estado;
  }
}

function pillVarianteEstadoVehiculo(estado: EstadoVehiculo | undefined | null) {
  switch (estado) {
    case 'aprobado':
      return 'success' as const;
    case 'pendiente_validacion':
      return 'pending' as const;
    case 'rechazado':
      return 'danger' as const;
    case 'inactivo':
    default:
      return 'info' as const;
  }
}

function labelEstadoVehiculo(estado: EstadoVehiculo | undefined | null) {
  // Sprint 18 P11: vocabulario validación, idem residente.
  switch (estado) {
    case 'aprobado':
      return 'Validado';
    case 'pendiente_validacion':
      return 'En revisión';
    case 'rechazado':
      return 'No validado';
    case 'inactivo':
      return 'Inactivo';
    default:
      return estado ?? '—';
  }
}

// Sprint 18 P5 B2 — controles inline de citofonía y wifi sobre la tarjeta
// del residente. Doctrina (docs/Aclaraciones-vecino.md §2):
// - Citofonía: 4 slots libres (H14). Cualquier residente —incluido el
//   titular— puede tomar cualquier slot 1-4 (el titular no está fijo al 1).
//   1 y 2 suenan en cascada; 3 y 4 solo abren el portón. Un residente por slot.
// - Citofonía: requiere CELULAR del residente. Es el número que se programa
//   en la central para que suene; sin él, el acceso llegaba a la worklist de
//   Reprogramación pidiéndole al tesorero teclear un número que no existe.
// - Wifi: requiere EMAIL del residente; máx 8 por casa. El correo es la
//   cuenta con la que entra a eWeLink y abre los portones.
// - Todo cambio crea una solicitud que la Comisión valida (estado
//   pendiente_validacion → activo); la baja libera el slot/cupo.
interface AccesosResidenteProps {
  tieneEmail: boolean;
  tieneCelular: boolean;
  /** Acceso de citofonía vivo (no de baja) de este residente, si lo hay. */
  citofoniaViva: AccesoCitofoniaRead | null;
  /** Acceso de wifi vivo (no de baja) de este residente, si lo hay. */
  wifiVivo: AccesoWifiRead | null;
  /** La casa llegó al máximo de wifis activos (no se puede agregar otro). */
  cupoWifiLleno: boolean;
  /** Slots de citofonía ocupados por OTROS residentes (no seleccionables). */
  slotsOcupadosPorOtros: number[];
  /** Cambia el slot de citofonía (null = quitar). El container orquesta
   *  baja del anterior + solicitud del nuevo. Devuelve error o null. */
  onCambiarCitofonia: (slotNuevo: number | null) => Promise<string | null>;
  /** Activa/desactiva el wifi del residente. Devuelve error o null. */
  onCambiarWifi: (activar: boolean) => Promise<string | null>;
}

function AccesosResidente({
  tieneEmail,
  tieneCelular,
  citofoniaViva,
  wifiVivo,
  cupoWifiLleno,
  slotsOcupadosPorOtros,
  onCambiarCitofonia,
  onCambiarWifi,
}: AccesosResidenteProps) {
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function ejecutar(fn: () => Promise<string | null>) {
    if (guardando) return;
    setGuardando(true);
    setError(null);
    const err = await fn();
    setError(err);
    setGuardando(false);
  }

  const slotActual = citofoniaViva?.slot ?? null;
  const citofoniaPendiente =
    citofoniaViva?.estado === 'pendiente_validacion';
  const wifiActivo = wifiVivo != null;
  const wifiPendiente = wifiVivo?.estado === 'pendiente_validacion';

  // H14: los 4 slots son libres para cualquier residente (incluido titular).
  const slotsCitofonia = [1, 2, 3, 4];

  return (
    <div className="mt-3 pt-2 border-t border-white/[0.06] space-y-2">
      <div className="text-[10px] uppercase tracking-wide text-text-muted">
        Accesos
      </div>

      {/* Citofonía — sin celular no se puede: es el número que se programa
          en la central. Se deshabilita acá en vez de dejar que el vecino lo
          elija y reciba un 409. */}
      <div className="flex items-center gap-2 flex-wrap text-sm">
        <span
          className="text-text-secondary"
          title={tieneCelular ? undefined : 'Requiere celular del residente'}
        >
          📞 Citofonía:
        </span>
        <select
          value={slotActual ?? ''}
          disabled={guardando || (!tieneCelular && slotActual === null)}
          aria-label="Slot de citofonía"
          onChange={(e) =>
            ejecutar(() =>
              onCambiarCitofonia(
                e.target.value ? Number(e.target.value) : null,
              ),
            )
          }
          className="bg-bg-card border-[0.5px] border-line-strong text-text-primary rounded-sm px-2 py-1 text-sm"
        >
          <option value="">Sin citofonía</option>
          {slotsCitofonia.map((s) => {
            const ocupado =
              slotsOcupadosPorOtros.includes(s) && s !== slotActual;
            return (
              <option key={s} value={s} disabled={ocupado}>
                Slot {s}
                {s <= 2 ? ' (suena)' : ' (solo portón)'}
                {ocupado ? ' — ocupado' : ''}
              </option>
            );
          })}
        </select>
        {!tieneCelular && slotActual === null && (
          <span className="text-xs text-text-muted">(requiere celular)</span>
        )}
        {citofoniaPendiente && (
          <Pill variant="pending">Pendiente validación</Pill>
        )}
      </div>

      {/* Wifi — al tope de la casa solo se permite quitar, no agregar. */}
      <div className="flex items-center gap-2 flex-wrap text-sm">
        <label
          className="inline-flex items-center gap-1.5 text-text-primary"
          title={tieneEmail ? undefined : 'Requiere email del residente'}
        >
          <input
            type="checkbox"
            checked={wifiActivo}
            disabled={
              guardando || !tieneEmail || (cupoWifiLleno && !wifiActivo)
            }
            onChange={(e) => ejecutar(() => onCambiarWifi(e.target.checked))}
          />
          📶 Acceso wifi
        </label>
        {/* El estado real del acceso: no basta con "la casilla está
            marcada". Entre que la Comisión valida y el acceso llega a
            eWeLink hay una ventana en que el vecino está autorizado y el
            portón todavía no lo reconoce; sin esto reclamaría que no le
            abre. */}
        {wifiVivo && (
          <EstadoWifiInline
            estado={(wifiVivo.estado_efectivo ?? 'activo') as EstadoWifi}
          />
        )}
        {!tieneEmail && (
          <span className="text-xs text-text-muted">(requiere email)</span>
        )}
        {/* Pista PREVENTIVA: solo antes de marcar, que es cuando sirve. Una
            vez activo seria ruido para los 96 accesos que funcionan.
            eWeLink no puede compartir con un correo sin cuenta creada, y el
            vecino no tiene como saberlo: al 2026-09-10 nueve residentes
            agotaron los cinco intentos por esto, cada uno con 24 horas de
            espera y un correo al titular que no esperaba.
            Texto corto a proposito — esto se lee en el celular. */}
        {!wifiActivo && tieneEmail && !cupoWifiLleno && (
          <span
            className="text-xs text-text-muted"
            title="eWeLink no puede compartir el acceso con un correo que no tiene cuenta. Se crea gratis en la aplicación eWeLink, con este mismo correo."
          >
            (necesita cuenta eWeLink)
          </span>
        )}
        {cupoWifiLleno && !wifiActivo && tieneEmail && (
          <span className="text-xs text-state-warning">
            (casa al máximo de {MAX_WIFI_POR_LOTE})
          </span>
        )}
        {wifiPendiente && <Pill variant="pending">Pendiente validación</Pill>}
      </div>

      {error && (
        <div className="text-xs text-state-danger" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

interface ResidenteCardProps {
  r: ResidenteConPersonaRead;
  // Sprint 17 S17-19 — recursos vivos asignados al residente
  // (típicamente llaveros con identificador, posibles wifi, etc).
  recursosAsignados: RecursoConSuspensionRead[];
  // Sprint 18 P4: si el usuario logueado es el titular del lote,
  // puede designar autorizados. El autorizado mismo NO puede.
  puedeDesignarAutorizado: boolean;
  onEditar: () => void;
  onEliminar: () => void;
  onToggleAutorizado: () => void;
  // Sprint 18 P5 B2 — accesos inline (citofonía + wifi).
  citofoniaViva: AccesoCitofoniaRead | null;
  wifiVivo: AccesoWifiRead | null;
  cupoWifiLleno: boolean;
  slotsOcupadosPorOtros: number[];
  onCambiarCitofonia: (slotNuevo: number | null) => Promise<string | null>;
  onCambiarWifi: (activar: boolean) => Promise<string | null>;
}

function ResidenteCard({
  r,
  recursosAsignados,
  puedeDesignarAutorizado,
  onEditar,
  onEliminar,
  onToggleAutorizado,
  citofoniaViva,
  wifiVivo,
  cupoWifiLleno,
  slotsOcupadosPorOtros,
  onCambiarCitofonia,
  onCambiarWifi,
}: ResidenteCardProps) {
  const editable = r.estado !== 'inactivo';
  // Sprint 17 Z22: 'pendiente_validacion' permite borrado físico (vecino
  // se arrepintió). 'aprobado' permite dar de baja (cierra residencia
  // con `hasta`). Otros estados son terminales históricos.
  const eliminable =
    r.estado === 'aprobado' || r.estado === 'pendiente_validacion';
  const etiquetaEliminar =
    r.estado === 'pendiente_validacion' ? 'Eliminar' : 'Dar de baja';
  // Sprint 18 P4: solo se ofrece designar/desdesignar sobre residentes
  // aprobados con email, que no sean el titular (el titular tiene
  // cuenta por su tenencia, no por flag).
  const designable =
    puedeDesignarAutorizado &&
    r.estado === 'aprobado' &&
    !!r.persona_email &&
    r.parentesco !== 'titular';
  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-base font-medium text-text-primary">
            {r.persona_nombre_completo}
          </div>
          <div className="text-xs text-text-muted mt-0.5">
            {PARENTESCO_LABEL[r.parentesco as ParentescoLiteral] ?? r.parentesco}
            {' · desde '}
            {r.desde}
          </div>
          {/* Badge del autorizado de portal. Desde el 2026-09-03 hay dos
              estados distintos: designado y esperando a la Comisión, o
              autorizado vigente. Mostrarlos igual dejaba al titular sin saber
              si su solicitud llegó. */}
          {r.cuenta_portal_activa && (
            <div className="mt-1.5 inline-flex items-center gap-1 text-xs text-orange">
              <span>🏠</span>
              <span>Autorizado de portal</span>
            </div>
          )}
          {!r.cuenta_portal_activa && r.designacion_autorizado_pendiente_id && (
            <div className="mt-1.5 inline-flex items-center gap-1 text-xs text-text-muted">
              <span>🕓</span>
              <span>Designado — esperando a la Comisión</span>
            </div>
          )}
        </div>
        <Pill variant={pillVarianteEstadoResidente(r.estado)}>
          {labelEstadoResidente(r.estado)}
        </Pill>
      </div>
      {r.motivo_rechazo && (
        <div className="text-xs text-state-danger mt-2">
          Motivo: {r.motivo_rechazo}
        </div>
      )}
      {/* Sprint 17 S17-19 — chips de recursos asignados al residente. */}
      {recursosAsignados.length > 0 && (
        <div className="mt-3 pt-2 border-t border-white/[0.06]">
          <div className="text-[10px] uppercase tracking-wide text-text-muted mb-1">
            Recursos asignados
          </div>
          <div className="flex flex-wrap gap-1.5">
            {recursosAsignados.map((rec) => (
              <span
                key={rec.id}
                className="text-[11px] px-2 py-0.5 rounded-sm bg-bg-sunken border-[0.5px] border-white/[0.08] text-text-secondary"
                title={`Recurso ${rec.identificador}`}
              >
                {RECURSO_TIPO_LABEL[rec.tipo]}{' '}
                <span className="font-mono">{rec.identificador}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      {/* Sprint 18 P5 B2 — citofonía + wifi inline. Solo residentes
          aprobados pueden tener accesos (el backend exige 'aprobado'). */}
      {r.estado === 'aprobado' && (
        <AccesosResidente
          tieneEmail={!!(r.persona_email ?? '').trim()}
          tieneCelular={!!(r.persona_celular ?? '').trim()}
          citofoniaViva={citofoniaViva}
          wifiVivo={wifiVivo}
          cupoWifiLleno={cupoWifiLleno}
          slotsOcupadosPorOtros={slotsOcupadosPorOtros}
          onCambiarCitofonia={onCambiarCitofonia}
          onCambiarWifi={onCambiarWifi}
        />
      )}
      {(editable || eliminable || designable) && (
        <div className="mt-3 flex justify-end gap-3">
          {designable && (
            <button
              type="button"
              onClick={onToggleAutorizado}
              className="text-xs text-orange hover:underline"
              title={
                r.cuenta_portal_activa
                  ? 'Quitar el acceso al portal de esta persona'
                  : r.designacion_autorizado_pendiente_id
                    ? 'Cancelar la designación que está esperando a la Comisión'
                    : 'Proponer a esta persona como autorizada. La Comisión debe aprobarlo.'
              }
            >
              {r.cuenta_portal_activa
                ? 'Quitar autorización'
                : r.designacion_autorizado_pendiente_id
                  ? 'Cancelar designación'
                  : 'Designar como autorizado'}
            </button>
          )}
          {editable && (
            <button
              type="button"
              onClick={onEditar}
              className="text-xs text-orange hover:underline"
            >
              Editar
            </button>
          )}
          {eliminable && (
            <button
              type="button"
              onClick={onEliminar}
              className="text-xs text-state-danger hover:underline"
            >
              {etiquetaEliminar}
            </button>
          )}
        </div>
      )}
    </Card>
  );
}

interface VehiculoCardProps {
  v: VehiculoRead;
  // Sprint 17 S17-19 — CR (u otro recurso) asignado a este vehículo.
  recursoAsignado: RecursoConSuspensionRead | null;
  onEditar: () => void;
  onEliminar: () => void;
}

function VehiculoCard({
  v,
  recursoAsignado,
  onEditar,
  onEliminar,
}: VehiculoCardProps) {
  const colorMostrado = v.color === 'Otro' ? v.color_otro : v.color;
  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-2">
          <Patente value={v.patente} verde={v.tipo_traccion !== 'combustion'} />
          <div className="text-sm text-text-secondary">
            {[v.marca, v.modelo].filter(Boolean).join(' ') || '—'}
            {colorMostrado && (
              <span className="text-text-muted ml-2">· {colorMostrado}</span>
            )}
          </div>
          {v.notas && (
            <div className="text-xs text-text-muted italic">{v.notas}</div>
          )}
        </div>
        <Pill variant={pillVarianteEstadoVehiculo(v.estado)}>
          {labelEstadoVehiculo(v.estado)}
        </Pill>
      </div>
      {v.motivo_rechazo && (
        <div className="text-xs text-state-danger mt-2">
          Motivo: {v.motivo_rechazo}
        </div>
      )}
      {/* Sprint 17 S17-19 — chip del CR asignado al vehículo. */}
      {recursoAsignado && (
        <div className="mt-3 pt-2 border-t border-white/[0.06]">
          <div className="text-[10px] uppercase tracking-wide text-text-muted mb-1">
            Control remoto asignado
          </div>
          <span
            className="text-[11px] px-2 py-0.5 rounded-sm bg-bg-sunken border-[0.5px] border-white/[0.08] text-text-secondary inline-flex items-center"
            title={`Recurso ${recursoAsignado.identificador}`}
          >
            <span className="font-mono">{recursoAsignado.identificador}</span>
          </span>
        </div>
      )}
      <div className="mt-3 flex justify-end gap-3">
        <button
          type="button"
          onClick={onEditar}
          className="text-xs text-orange hover:underline"
        >
          Editar
        </button>
        <button
          type="button"
          onClick={onEliminar}
          className="text-xs text-state-danger hover:underline"
        >
          Eliminar
        </button>
      </div>
    </Card>
  );
}

// ============================================================
// Página principal
// ============================================================

export interface MisDatosBodyProps {
  me: AuthMeResponse;
  loteIdSeleccionado: string;
  onLoteIdChange: (loteId: string) => void;
  residentes: ResidenteConPersonaRead[];
  vehiculos: VehiculoRead[];
  recursos: RecursoConSuspensionRead[];
  // Sprint 18 P5 B2 — accesos del lote (citofonía + wifi).
  accesosCitofonia: AccesoCitofoniaRead[];
  accesosWifi: AccesoWifiRead[];
  // Sprint 18 P23 — resumen del lote arrendado (solo propietario_no_residente).
  resumenArrendado: ResumenArrendado | null;
  loadingLote: boolean;
  errorLote: string | null;
  /** Submit del form de datos personales. Devuelve null si OK, mensaje
   *  de error para mostrar inline si falla. Sprint 14 S14-11. */
  onGuardarPersona: (email: string, celular: string) => Promise<string | null>;
  /** Confirma "Marcar como salido" con fecha YYYY-MM-DD ya validada. */
  onInactivarResidente: (r: ResidenteConPersonaRead, hasta: string) => Promise<string | null>;
  /** Confirma "Eliminar vehículo". */
  onEliminarVehiculo: (v: VehiculoRead) => Promise<string | null>;
  /** Trigger refetch después del modal de residente. */
  onResidenteAgregado: () => void;
  /** Trigger refetch después del modal de vehículo. */
  onVehiculoGuardado: () => void;
  /** Sprint 18 P5 B2 — cambia el slot de citofonía de un residente
   *  (orquesta baja del anterior + solicitud del nuevo). */
  onCambiarCitofonia: (
    r: ResidenteConPersonaRead,
    accesoVivo: AccesoCitofoniaRead | null,
    slotNuevo: number | null,
  ) => Promise<string | null>;
  /** Sprint 18 P5 B2 — activa/desactiva el wifi de un residente. */
  onCambiarWifi: (
    r: ResidenteConPersonaRead,
    accesoVivo: AccesoWifiRead | null,
    activar: boolean,
  ) => Promise<string | null>;
  /** Sprint 18 P3 B3: callback tras cerrar la propia tenencia.
   *  El container refresca /auth/me + refetch del lote. */
  onTenenciaCerrada?: () => void;
}

/**
 * Body presentacional de Mis Datos — Sprint 14 S14-11.
 *
 * Recibe data + callbacks como props. NO usa `useAuth()` ni hace fetch.
 * El container (`MisDatos`) abajo orquesta los side effects. El preview
 * `/dev/mis-datos` lo monta con mock data y handlers no-op.
 */
export function MisDatosBody({
  me,
  loteIdSeleccionado,
  onLoteIdChange,
  residentes,
  vehiculos,
  recursos,
  accesosCitofonia,
  accesosWifi,
  resumenArrendado,
  loadingLote,
  errorLote,
  onGuardarPersona,
  onInactivarResidente,
  onEliminarVehiculo,
  onResidenteAgregado,
  onVehiculoGuardado,
  onTenenciaCerrada,
  onCambiarCitofonia,
  onCambiarWifi,
}: MisDatosBodyProps) {
  const tenencias = me.tenencias_activas;

  // Form datos personales (autoedición)
  const [emailEdit, setEmailEdit] = useState(me.persona.email ?? '');
  const [celularEdit, setCelularEdit] = useState(me.persona.celular ?? '');
  const [guardandoPersona, setGuardandoPersona] = useState(false);
  const [errorPersona, setErrorPersona] = useState<string | null>(null);
  const [okPersona, setOkPersona] = useState(false);

  // Modales
  const [modalResidente, setModalResidente] = useState(false);
  // S15-Vec.3: modal de edición del residente. Distinto del modal de
  // agregar (que crea persona+residente desde cero).
  const [editandoResidente, setEditandoResidente] =
    useState<ResidenteConPersonaRead | null>(null);
  const [modalVehiculo, setModalVehiculo] = useState<{
    abierto: boolean;
    inicial: VehiculoRead | null;
  }>({ abierto: false, inicial: null });
  // Sprint 17 S17-16 — modal del vecino para solicitar un recurso nuevo.
  const [modalSolicitarRecurso, setModalSolicitarRecurso] = useState(false);
  // Sprint 18 P8 — recurso sobre el que el vecino abre "Reportar problema".
  const [recursoAReportar, setRecursoAReportar] =
    useState<RecursoConSuspensionRead | null>(null);
  const [solicitudCreadaMsg, setSolicitudCreadaMsg] = useState<string | null>(
    null,
  );

  // S14-09: tabs internos del Mis Datos.
  type Tab = 'datos' | 'residentes' | 'vehiculos';
  const [tab, setTab] = useState<Tab>('datos');

  const loteSeleccionado = useMemo(
    () => tenencias.find((t) => t.lote_id === loteIdSeleccionado),
    [tenencias, loteIdSeleccionado],
  );

  async function handleGuardarPersona(e: FormEvent) {
    e.preventDefault();
    setGuardandoPersona(true);
    setErrorPersona(null);
    setOkPersona(false);
    const err = await onGuardarPersona(emailEdit.trim(), celularEdit.trim());
    if (err) setErrorPersona(err);
    else setOkPersona(true);
    setGuardandoPersona(false);
  }

  // Sprint 17 Z22: state del modal de baja/eliminación de residente.
  const [residenteAProcesar, setResidenteAProcesar] =
    useState<ResidenteConPersonaRead | null>(null);
  const [hastaInput, setHastaInput] = useState('');
  const [procesandoResidente, setProcesandoResidente] = useState(false);
  const [errorProcesarResidente, setErrorProcesarResidente] = useState<
    string | null
  >(null);

  function handleInactivarResidenteClick(r: ResidenteConPersonaRead) {
    setResidenteAProcesar(r);
    setHastaInput(hoy());
    setErrorProcesarResidente(null);
  }

  function cerrarModalResidente() {
    setResidenteAProcesar(null);
    setHastaInput('');
    setErrorProcesarResidente(null);
  }

  async function confirmarProcesarResidente() {
    if (!residenteAProcesar || procesandoResidente) return;
    setProcesandoResidente(true);
    setErrorProcesarResidente(null);
    try {
      if (residenteAProcesar.estado === 'pendiente_validacion') {
        await eliminarResidente(residenteAProcesar.id);
        onResidenteAgregado();
        cerrarModalResidente();
      } else {
        // aprobado → cerrar con `hasta`.
        if (!/^\d{4}-\d{2}-\d{2}$/.test(hastaInput)) {
          setErrorProcesarResidente('Fecha inválida.');
          setProcesandoResidente(false);
          return;
        }
        const err = await onInactivarResidente(residenteAProcesar, hastaInput);
        if (err) {
          setErrorProcesarResidente(err);
        } else {
          cerrarModalResidente();
        }
      }
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setErrorProcesarResidente(detail);
      } else {
        setErrorProcesarResidente('Error de red al procesar.');
      }
    } finally {
      setProcesandoResidente(false);
    }
  }

  // Sprint 18 P3 B3: modal "Dejé el barrio" + feedback post-cierre.
  const [modalDejeBarrio, setModalDejeBarrio] = useState(false);
  // Sprint 18 P10 — Ley 21.719: descarga de datos + solicitud de eliminación.
  const [descargandoDatos, setDescargandoDatos] = useState(false);
  const [modalEliminacion, setModalEliminacion] = useState(false);
  const [motivoEliminacion, setMotivoEliminacion] = useState('');
  const [enviandoEliminacion, setEnviandoEliminacion] = useState(false);
  const [errorLey, setErrorLey] = useState<string | null>(null);
  const [leyMsg, setLeyMsg] = useState<string | null>(null);

  async function handleDescargarDatos() {
    if (descargandoDatos) return;
    setDescargandoDatos(true);
    setErrorLey(null);
    try {
      await descargarMisDatos();
    } catch {
      setErrorLey('No pudimos generar la descarga. Intenta de nuevo.');
    } finally {
      setDescargandoDatos(false);
    }
  }

  async function handleSolicitarEliminacion() {
    if (enviandoEliminacion) return;
    setEnviandoEliminacion(true);
    setErrorLey(null);
    try {
      await solicitarEliminacion(motivoEliminacion.trim() || null);
      setModalEliminacion(false);
      setMotivoEliminacion('');
      setLeyMsg(
        'Solicitud enviada. La Comisión la revisará y resolverá tu eliminación.',
      );
    } catch (err) {
      if (err instanceof ApiError) {
        setErrorLey(
          typeof err.body === 'object' &&
            err.body !== null &&
            'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`,
        );
      } else {
        setErrorLey('Error de red al enviar la solicitud.');
      }
    } finally {
      setEnviandoEliminacion(false);
    }
  }
  const [cierreOk, setCierreOk] = useState<{
    residentes: number;
    vehiculos: number;
    recursos: number;
    solicitudes: number;
  } | null>(null);

  // Sprint 18 P6: modal de confirmación para eliminar vehículo
  // (reemplaza window.confirm + alert que violaba doctrina TODO POR UI).
  const [vehiculoAEliminar, setVehiculoAEliminar] =
    useState<VehiculoRead | null>(null);
  const [eliminandoVehiculo, setEliminandoVehiculo] = useState(false);
  const [errorEliminarVehiculo, setErrorEliminarVehiculo] = useState<
    string | null
  >(null);

  function handleEliminarVehiculoClick(v: VehiculoRead) {
    setVehiculoAEliminar(v);
    setErrorEliminarVehiculo(null);
  }

  // Autorizado de portal. Desde el 2026-09-03 designar NO crea la cuenta:
  // abre un modal que pide la justificación y deja la designación pendiente
  // de la Comisión. Quitar la autorización y cancelar una designación
  // pendiente siguen siendo directos: no crean accesos, los quitan.
  const [designando, setDesignando] =
    useState<ResidenteConPersonaRead | null>(null);
  const [errorDesignacion, setErrorDesignacion] = useState<string | null>(null);

  function _mensajeError(err: unknown, fallback: string): string {
    return err instanceof ApiError &&
      typeof err.body === 'object' &&
      err.body !== null &&
      'detail' in err.body
      ? String((err.body as { detail: unknown }).detail)
      : fallback;
  }

  async function handleToggleAutorizado(r: ResidenteConPersonaRead) {
    if (r.cuenta_portal_activa) {
      try {
        await desdesignarAutorizado(r.id);
        onResidenteAgregado();
      } catch (err) {
        setErrorProcesarResidente(
          _mensajeError(err, 'Error al quitar la autorización.'),
        );
      }
      return;
    }
    if (r.designacion_autorizado_pendiente_id) {
      try {
        await cancelarDesignacionAutorizado(
          r.designacion_autorizado_pendiente_id,
        );
        onResidenteAgregado();
      } catch (err) {
        setErrorProcesarResidente(
          _mensajeError(err, 'Error al cancelar la designación.'),
        );
      }
      return;
    }
    setDesignando(r);
  }

  async function confirmarDesignacion(justificacion: string) {
    if (!designando) return;
    try {
      await designarAutorizado(designando.id, justificacion);
      setDesignando(null);
      onResidenteAgregado();
    } catch (err) {
      // El modal queda ABIERTO con el error: si el titular escribió un motivo
      // largo, cerrarlo se lo haría perder.
      setErrorDesignacion(
        _mensajeError(err, 'Error al enviar la designación.'),
      );
    }
  }

  function cerrarModalEliminarVehiculo() {
    setVehiculoAEliminar(null);
    setErrorEliminarVehiculo(null);
  }

  async function confirmarEliminarVehiculo() {
    if (!vehiculoAEliminar || eliminandoVehiculo) return;
    setEliminandoVehiculo(true);
    setErrorEliminarVehiculo(null);
    const err = await onEliminarVehiculo(vehiculoAEliminar);
    setEliminandoVehiculo(false);
    if (err) {
      setErrorEliminarVehiculo(err);
    } else {
      setVehiculoAEliminar(null);
    }
  }

  // Sin tenencias activas
  if (tenencias.length === 0) {
    return (
      <div>
        <div className="bg-bg-sunken border-b-[0.5px] border-white/[0.08] px-4 md:px-8 pt-6 pb-5">
          <div className="text-sm text-text-muted uppercase tracking-wide mb-1">
            Mis datos
          </div>
          <h1 className="text-2xl font-medium text-text-primary leading-tight">
            {me.persona.nombre_completo}
          </h1>
        </div>
        <div className="px-4 md:px-8 py-6 max-w-2xl">
          <div className="bg-state-info/15 border-l-4 border-state-info p-3 rounded-sm text-sm text-text-primary">
            No tienes una casa asignada actualmente. Si esto es un error,
            contacta a la Comisión.
          </div>
        </div>
      </div>
    );
  }

  // Sprint 18 P23: el propietario no residente (arrienda, no vive) tiene
  // vista restringida — no gestiona el hogar (residentes/vehículos/accesos/
  // recursos son del arrendatario). Ve solo sus datos + resumen de la casa.
  const esNoResidente =
    loteSeleccionado?.tipo === 'propietario_no_residente';

  // S14-09 tabs: defininción del contador a mostrar al lado de cada tab.
  // 0 → nada (no se muestra el contador); n>0 → "(n)".
  // P23: el no residente solo ve el tab "Mis datos".
  const tabs: { id: Tab; label: string; count: number }[] = esNoResidente
    ? [{ id: 'datos', label: 'Mis datos', count: 0 }]
    : [
        { id: 'datos', label: 'Mis datos', count: 0 },
        { id: 'residentes', label: 'Residentes', count: residentes.length },
        { id: 'vehiculos', label: 'Vehículos', count: vehiculos.length },
      ];
  // Tab efectivo: si quedó en uno oculto (cambió el lote), forzar 'datos'.
  const tabActivo: Tab = esNoResidente ? 'datos' : tab;

  return (
    <div>
      {/* Header */}
      <div className="bg-bg-sunken border-b-[0.5px] border-white/[0.08] px-4 md:px-8 pt-6 pb-4">
        <div className="text-sm text-text-muted uppercase tracking-wide mb-1">
          Mis datos
        </div>
        <h1 className="text-2xl font-medium text-text-primary leading-tight">
          {me.persona.nombre_completo}
        </h1>
        {loteSeleccionado && (
          <p className="text-base text-text-secondary mt-1">
            {capitalizar(loteSeleccionado.lote_sector)}{' '}
            {loteSeleccionado.lote_identificador}
          </p>
        )}

        {tenencias.length > 1 && (
          <label className="block text-sm text-text-secondary mt-3">
            <span className="mr-2">Casa:</span>
            <select
              value={loteIdSeleccionado}
              onChange={(e) => onLoteIdChange(e.target.value)}
              className="bg-bg-card border-[0.5px] border-white/15 rounded-sm px-2 py-1 text-text-primary"
            >
              {tenencias.map((t) => (
                <option key={t.lote_id} value={t.lote_id}>
                  {capitalizar(t.lote_sector)} {t.lote_identificador}
                </option>
              ))}
            </select>
          </label>
        )}

        {/* S14-09: tabs internos. S14-11: overflow-x-auto + whitespace-nowrap
            como defensa si los counts crecen y los 3 tabs no caben en 375px. */}
        <div
          className="flex gap-1 mt-5 -mb-[1px] overflow-x-auto flex-nowrap"
          role="tablist"
        >
          {tabs.map((t) => {
            const activo = tabActivo === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={activo}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activo
                    ? 'text-orange border-orange'
                    : 'text-text-secondary border-transparent hover:text-text-primary'
                }`}
              >
                {t.label}
                {t.count > 0 && (
                  <span className="ml-1 text-text-muted font-normal">
                    ({t.count})
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-4 md:px-8 py-6 space-y-6 max-w-4xl">
        {/* Sprint 18 P23 — casa arrendada: resumen read-only (atenuado). El
            dueño no residente no gestiona el hogar; solo ve cantidades. */}
        {esNoResidente && loteSeleccionado && (
          <Section>
            <SectionHead title="Casa que arriendas" />
            <Card className="opacity-90">
              <p className="text-sm text-text-secondary mb-3">
                Esta casa la habita{' '}
                <span className="text-text-primary font-medium">
                  {resumenArrendado?.arrendatario_nombre ?? 'tu arrendatario'}
                </span>
                . La gestión del hogar (residentes, vehículos, accesos y
                recursos) la realiza quien la habita; tú ves solo el resumen.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  ['Residentes', resumenArrendado?.n_residentes ?? 0],
                  ['Vehículos', resumenArrendado?.n_vehiculos ?? 0],
                  ['Llaveros', resumenArrendado?.n_llaveros ?? 0],
                  ['Controles', resumenArrendado?.n_controles ?? 0],
                ].map(([label, n]) => (
                  <div
                    key={label}
                    className="bg-bg-sunken/50 border-[0.5px] border-white/[0.08] rounded-sm px-3 py-2 text-center"
                  >
                    <div className="text-xl font-medium text-text-primary">
                      {n}
                    </div>
                    <div className="text-xs text-text-muted">{label}</div>
                  </div>
                ))}
              </div>
            </Card>
            <InfoNote>
              Sigues siendo el dueño y responsable de las cuotas de esta casa.
              Ves tus cuotas y pagos en "Mi cuenta".
            </InfoNote>
          </Section>
        )}

        {/* === Tab: Mis datos (Datos personales) === */}
        {tabActivo === 'datos' && (
        <section>
          <Card>
            <form onSubmit={handleGuardarPersona} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-text-secondary block mb-1">
                    Nombre completo
                  </label>
                  <input
                    type="text"
                    value={me.persona.nombre_completo}
                    disabled
                    className="w-full rounded-sm bg-bg-sunken/50 border-[0.5px] border-white/[0.08] text-text-muted px-3 py-2 cursor-not-allowed"
                  />
                  <span className="text-xs text-text-muted">
                    Para cambiarlo, contacta a la Comisión.
                  </span>
                </div>
                <div>
                  <label className="text-sm text-text-secondary block mb-1">
                    RUT
                  </label>
                  <input
                    type="text"
                    value={formatRut(me.persona.rut) || '—'}
                    disabled
                    className="w-full rounded-sm bg-bg-sunken/50 border-[0.5px] border-white/[0.08] text-text-muted px-3 py-2 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="text-sm text-text-secondary block mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={emailEdit}
                    onChange={(e) => setEmailEdit(e.target.value)}
                    maxLength={255}
                    className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2 focus:border-orange focus:outline-none focus:ring-1 focus:ring-orange/30"
                  />
                </div>
                <div>
                  <label htmlFor="celular-edit" className="text-sm text-text-secondary block mb-1">
                    Celular
                  </label>
                  <InputCelular
                    id="celular-edit"
                    value={celularEdit}
                    onChange={setCelularEdit}
                  />
                </div>
              </div>

              {errorPersona && (
                <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
                  {errorPersona}
                </div>
              )}
              {okPersona && (
                <div className="bg-state-success/15 border-l-4 border-state-success p-3 rounded-sm text-sm text-text-primary">
                  Datos actualizados.
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={guardandoPersona}
                >
                  {guardandoPersona ? 'Guardando…' : 'Guardar cambios'}
                </Button>
              </div>
            </form>
          </Card>
          <InfoNote>
            Para cambiar nombre o RUT, contacta a la Comisión. Email y celular
            puedes actualizarlos tú directamente.
          </InfoNote>
        </section>
        )}

        {/* === Loading / Error de lote === */}
        {loadingLote && (
          <p className="text-sm text-text-muted">Cargando datos de la casa…</p>
        )}
        {errorLote && (
          <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
            {errorLote}
          </div>
        )}

        {!loadingLote && !errorLote && (
          <>
            {/* === Tab: Residentes === */}
            {tabActivo === 'residentes' && (
              <section>
                <Section>
                  <SectionHead title="Residentes de la casa" />
                  {residentes.length === 0 ? (
                    <EmptyState
                      title="Aún no has agregado residentes"
                      description="Registra a las personas que viven contigo. La Comisión deberá aprobarlas antes de que queden activas."
                      action={
                        <Button
                          variant="primary"
                          onClick={() => setModalResidente(true)}
                        >
                          + Agregar primer residente
                        </Button>
                      }
                    />
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {(() => {
                        // Sprint 18 P5 B2 — tope wifi de la casa (cuenta
                        // accesos activos del lote contra el máximo).
                        const cupoWifiLleno =
                          accesosWifi.filter((a) => a.estado === 'activo')
                            .length >= MAX_WIFI_POR_LOTE;
                        return residentes.map((r) => {
                        // Sprint 18 P5 B2 — accesos vivos (no de baja) de
                        // este residente + slots ocupados por otros.
                        const citofoniaViva =
                          accesosCitofonia.find(
                            (a) =>
                              a.residente_id === r.id && a.estado !== 'baja',
                          ) ?? null;
                        const wifiVivo =
                          accesosWifi.find(
                            (a) =>
                              a.residente_id === r.id && a.estado !== 'baja',
                          ) ?? null;
                        const slotsOcupadosPorOtros = accesosCitofonia
                          .filter(
                            (a) =>
                              a.estado !== 'baja' && a.residente_id !== r.id,
                          )
                          .map((a) => a.slot);
                        return (
                          <ResidenteCard
                            key={r.id}
                            r={r}
                            recursosAsignados={recursos.filter(
                              (rec) =>
                                rec.residente_actual_id === r.id &&
                                rec.estado === 'entregado',
                            )}
                            // Sprint 18 P4: si el usuario logueado tiene
                            // tenencia activa en este lote, es titular y
                            // puede designar autorizados.
                            puedeDesignarAutorizado={!!loteSeleccionado}
                            onEditar={() => setEditandoResidente(r)}
                            onEliminar={() => handleInactivarResidenteClick(r)}
                            onToggleAutorizado={() => handleToggleAutorizado(r)}
                            citofoniaViva={citofoniaViva}
                            wifiVivo={wifiVivo}
                            cupoWifiLleno={cupoWifiLleno}
                            slotsOcupadosPorOtros={slotsOcupadosPorOtros}
                            onCambiarCitofonia={(slotNuevo) =>
                              onCambiarCitofonia(r, citofoniaViva, slotNuevo)
                            }
                            onCambiarWifi={(activar) =>
                              onCambiarWifi(r, wifiVivo, activar)
                            }
                          />
                        );
                        });
                      })()}
                      <AddCard
                        label="Agregar otro residente"
                        onClick={() => setModalResidente(true)}
                      />
                    </div>
                  )}
                </Section>
                <InfoNote>
                  Los menores de edad se registran sin contacto. Si un residente
                  ya no vive en la casa, usa "Eliminar" para cerrar su tenencia;
                  el historial se conserva.
                  <br />
                  <strong>Citofonía:</strong> los slots 1 y 2 reciben la llamada
                  del portón en cascada; los slots 3 y 4 solo abren el portón.{' '}
                  <em>Ejemplo:</em> tu celular en el slot 1 y el de tu pareja en
                  el slot 2 — si no contestas tú, suena ella.{' '}
                  <strong>Wifi</strong> abre los portones y puertas peatonales
                  desde el celular y requiere email. Cada cambio lo valida la
                  Comisión.
                </InfoNote>
              </section>
            )}

            {/* === Tab: Vehículos === */}
            {tabActivo === 'vehiculos' && (
              <section>
                <Section>
                  <SectionHead title="Vehículos del hogar" />
                  {vehiculos.length === 0 ? (
                    <EmptyState
                      title="No tienes vehículos registrados"
                      description="Agrega tus autos y motos para que el control de acceso los reconozca al entrar al barrio."
                      action={
                        <Button
                          variant="primary"
                          onClick={() =>
                            setModalVehiculo({ abierto: true, inicial: null })
                          }
                        >
                          + Agregar primer vehículo
                        </Button>
                      }
                    />
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {vehiculos.map((v) => (
                        <VehiculoCard
                          key={v.id}
                          v={v}
                          recursoAsignado={
                            recursos.find(
                              (rec) =>
                                rec.vehiculo_id === v.id &&
                                rec.estado === 'entregado',
                            ) ?? null
                          }
                          onEditar={() =>
                            setModalVehiculo({ abierto: true, inicial: v })
                          }
                          onEliminar={() => handleEliminarVehiculoClick(v)}
                        />
                      ))}
                      <AddCard
                        label="Agregar otro vehículo"
                        onClick={() =>
                          setModalVehiculo({ abierto: true, inicial: null })
                        }
                      />
                    </div>
                  )}
                </Section>
                <InfoNote>
                  Registrar tus vehículos ayuda a la seguridad del barrio
                  (identificar qué autos pertenecen a vecinos) y entrega
                  información importante a Bomberos ante una emergencia. Si
                  cambias de vehículo, recuerda actualizar la información.
                </InfoNote>
              </section>
            )}

            {/* === Recursos asignados (oculto para el no residente: P23) === */}
            {tabActivo === 'datos' && !esNoResidente && (
              <section>
                {solicitudCreadaMsg && (
                  <div
                    className="bg-state-success/15 border-l-4 border-state-success p-3 rounded-sm text-sm text-text-primary mb-3"
                    role="status"
                  >
                    {solicitudCreadaMsg}{' '}
                    <button
                      type="button"
                      onClick={() => setSolicitudCreadaMsg(null)}
                      className="underline ml-2"
                    >
                      Cerrar
                    </button>
                  </div>
                )}
                {/* Rediseño smoke Fase 8: bolsa (sin asignar) + asignados
                    (lista compacta con a-quién), sin repetir recursos. */}
                <MisRecursos
                  recursos={recursos}
                  residentes={residentes}
                  vehiculos={vehiculos}
                  onAsignado={onVehiculoGuardado}
                  onReportar={(r) => setRecursoAReportar(r)}
                  onSolicitar={() => setModalSolicitarRecurso(true)}
                />
                <InfoNote>
                  Si pierdes un recurso o necesitas uno adicional, usa el
                  botón <strong>+ Solicitar recurso</strong>. Tu solicitud
                  llega a la Comisión para revisión.
                </InfoNote>
              </section>
            )}
          </>
        )}

        {/* F13: acciones de cierre al final del tab "datos" — después de
            "Recursos de tu casa". "Dejar el barrio" + derechos Ley 21.719. */}
        {tabActivo === 'datos' && (
          <section>
            {/* "Dejé el barrio" — solo si la tenencia actual lo permite por
                tipo. El propietario_no_residente sale por comisión. */}
            {loteSeleccionado &&
              ['propietario_residente', 'arrendatario', 'comodatario'].includes(
                loteSeleccionado.tipo,
              ) && (
                <Section>
                  <SectionHead title="Dejar el barrio" />
                  <Card>
                    <p className="text-sm text-text-secondary mb-3">
                      Si vendiste tu propiedad o terminó tu arriendo, puedes
                      cerrar tu vínculo con la Casa{' '}
                      <span className="text-text-primary font-medium">
                        {loteSeleccionado.lote_identificador}
                      </span>
                      . La Comisión recibirá aviso para bajar tu acceso a
                      citofonía y WiFi.
                    </p>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => setModalDejeBarrio(true)}
                        disabled={!!cierreOk}
                        className="text-sm text-state-danger hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Dejé el barrio
                      </button>
                    </div>
                  </Card>
                </Section>
              )}

            {/* Mis derechos (Ley 21.719) — siempre visible bajo "datos". */}
            <Section>
              <SectionHead title="Mis derechos sobre mis datos" />
              {leyMsg && (
                <div
                  className="bg-state-success/15 border-l-4 border-state-success p-3 rounded-sm text-sm text-text-primary mb-3"
                  role="status"
                >
                  {leyMsg}
                </div>
              )}
              <Card>
                <p className="text-sm text-text-secondary mb-3">
                  La Ley 21.719 te da derecho a obtener una copia de los datos
                  que el barrio tiene sobre ti y a pedir que se eliminen. Conoce
                  más en{' '}
                  <a href="/privacidad" className="text-orange hover:underline">
                    nuestra política de privacidad
                  </a>
                  .
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={handleDescargarDatos}
                    disabled={descargandoDatos}
                  >
                    {descargandoDatos ? 'Generando…' : 'Descargar mis datos'}
                  </Button>
                  <button
                    type="button"
                    onClick={() => {
                      setErrorLey(null);
                      setModalEliminacion(true);
                    }}
                    className="text-sm text-state-danger hover:underline"
                  >
                    Solicitar eliminación de mi cuenta
                  </button>
                </div>
                {errorLey && (
                  <p className="text-xs text-state-danger mt-3" role="alert">
                    {errorLey}
                  </p>
                )}
              </Card>
              <InfoNote>
                La eliminación de cuenta requiere que primero hayas dejado todas
                tus casas. La Comisión evalúa cada solicitud (puede haber deudas
                o tenencias activas) y anonimiza tus datos conservando solo lo
                contable, sin identificarte.
              </InfoNote>
            </Section>
          </section>
        )}
      </div>

      {/* Modales */}
      {modalResidente && loteIdSeleccionado && (
        <ModalAgregarResidente
          loteId={loteIdSeleccionado}
          hayLlaverosEnBolsa={llaverosEnBolsa(recursos).length > 0}
          onCerrar={() => setModalResidente(false)}
          onAgregado={() => {
            setModalResidente(false);
            onResidenteAgregado();
          }}
        />
      )}

      {modalVehiculo.abierto && loteIdSeleccionado && (
        <ModalVehiculo
          loteId={loteIdSeleccionado}
          inicial={modalVehiculo.inicial}
          hayControlesEnBolsa={controlesEnBolsa(recursos).length > 0}
          onCerrar={() => setModalVehiculo({ abierto: false, inicial: null })}
          onGuardado={() => {
            setModalVehiculo({ abierto: false, inicial: null });
            onVehiculoGuardado();
          }}
        />
      )}

      {/* Sprint 17 S17-16 — modal del vecino para solicitar recurso. */}
      {modalSolicitarRecurso && loteIdSeleccionado && (
        <ModalSolicitarRecurso
          loteId={loteIdSeleccionado}
          residentes={residentes.filter(
            (r) => r.estado === 'aprobado' && (r.hasta === null || r.hasta >= hoy()),
          )}
          vehiculos={vehiculos.filter((v) => v.activo)}
          recursos={recursos}
          onCerrar={() => setModalSolicitarRecurso(false)}
          onCreada={() => {
            setModalSolicitarRecurso(false);
            setSolicitudCreadaMsg(
              'Solicitud enviada a la Comisión. Recibirás novedades cuando la revisen.',
            );
          }}
        />
      )}

      {/* Sprint 18 P8 — modal del vecino para reportar problema de recurso. */}
      {recursoAReportar && (
        <ModalReportarProblema
          recurso={recursoAReportar}
          etiquetaTipo={RECURSO_TIPO_LABEL[recursoAReportar.tipo]}
          onCerrar={() => setRecursoAReportar(null)}
          onReportado={() => {
            setRecursoAReportar(null);
            setSolicitudCreadaMsg(
              'Reporte enviado. La Comisión lo revisará y coordinará la reposición.',
            );
            onResidenteAgregado();
          }}
        />
      )}

      {editandoResidente && (
        <ModalEditarResidente
          residente={editandoResidente}
          onCerrar={() => setEditandoResidente(null)}
          onGuardado={() => {
            setEditandoResidente(null);
            onResidenteAgregado();
          }}
        />
      )}

      {/* Sprint 17 Z22: modal eliminar / dar de baja residente */}
      {residenteAProcesar && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-md w-full space-y-4">
            <div className="flex items-start justify-between">
              <h2 className="text-lg font-medium text-text-primary">
                {residenteAProcesar.estado === 'pendiente_validacion'
                  ? 'Eliminar residente'
                  : 'Dar de baja residente'}
              </h2>
              <button
                type="button"
                onClick={cerrarModalResidente}
                disabled={procesandoResidente}
                className="text-text-muted hover:text-text-primary text-xl leading-none disabled:opacity-50"
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <p className="text-sm text-text-secondary">
              {residenteAProcesar.estado === 'pendiente_validacion' ? (
                <>
                  Vas a borrar a{' '}
                  <span className="text-text-primary font-medium">
                    {residenteAProcesar.persona_nombre_completo}
                  </span>{' '}
                  antes de que la Comisión la apruebe. Si tiene
                  solicitudes de recurso en curso (llaveros, controles),
                  también se cancelarán. Esta acción no es reversible.
                </>
              ) : (
                <>
                  Indica desde qué fecha{' '}
                  <span className="text-text-primary font-medium">
                    {residenteAProcesar.persona_nombre_completo}
                  </span>{' '}
                  dejó de habitar la casa.
                </>
              )}
            </p>
            {residenteAProcesar.estado === 'aprobado' && (
              <label className="text-sm space-y-1 block">
                <span className="text-text-secondary">Fecha de baja</span>
                <InputFecha
                  value={hastaInput}
                  onChange={setHastaInput}
                  max={hoy()}
                  disabled={procesandoResidente}
                  className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
                  requerido
                />
              </label>
            )}
            {errorProcesarResidente && (
              <div
                className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
                role="alert"
              >
                {errorProcesarResidente}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={cerrarModalResidente}
                disabled={procesandoResidente}
                className="px-4 py-2 text-sm text-text-secondary hover:bg-bg-sunken rounded-sm disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmarProcesarResidente}
                disabled={procesandoResidente}
                className="px-4 py-2 text-sm bg-state-danger hover:bg-state-danger/80 text-white font-medium rounded-sm disabled:opacity-50"
              >
                {procesandoResidente
                  ? 'Procesando…'
                  : residenteAProcesar.estado === 'pendiente_validacion'
                    ? 'Sí, eliminar'
                    : 'Confirmar baja'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sprint 18 P3 B3: modal "Dejé el barrio". */}
      {modalDejeBarrio && loteSeleccionado && (
        <ModalDejeBarrio
          tenenciaId={loteSeleccionado.id}
          tipoTenencia={loteSeleccionado.tipo}
          loteIdentificador={loteSeleccionado.lote_identificador}
          onCerrar={() => setModalDejeBarrio(false)}
          onCerrado={(resultado) => {
            setModalDejeBarrio(false);
            setCierreOk({
              residentes: resultado.residentes_inactivados,
              vehiculos: resultado.vehiculos_inactivados,
              recursos: resultado.recursos_a_bolsa,
              solicitudes: resultado.solicitudes_canceladas,
            });
            // Refrescar /auth/me + lote (el container resuelve cómo).
            onTenenciaCerrada?.();
            onResidenteAgregado();
          }}
        />
      )}

      {/* Sprint 18 P10 — confirmación de solicitud de eliminación de cuenta. */}
      {modalEliminacion && (
        <Modal
          title="Solicitar eliminación de mi cuenta"
          onClose={() => setModalEliminacion(false)}
          footer={
            <>
              <Button
                variant="ghost"
                onClick={() => setModalEliminacion(false)}
                disabled={enviandoEliminacion}
              >
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={handleSolicitarEliminacion}
                disabled={enviandoEliminacion}
              >
                {enviandoEliminacion ? 'Enviando…' : 'Enviar solicitud'}
              </Button>
            </>
          }
        >
          <p className="text-sm text-text-primary mb-3">
            Tu solicitud llega a la Comisión, que la evaluará. Si la aprueba,
            tus datos personales se anonimizan (se conserva solo lo contable,
            sin identificarte). Necesitas haber dejado todas tus casas primero.
          </p>
          <label className="block text-sm text-text-secondary mb-1">
            Motivo (opcional)
          </label>
          <textarea
            value={motivoEliminacion}
            onChange={(e) => setMotivoEliminacion(e.target.value)}
            rows={3}
            className="w-full bg-bg-card border-[0.5px] border-line-strong text-text-primary rounded-sm px-3 py-2 text-sm"
            placeholder="Ej: ya no vivo en el barrio"
          />
          {errorLey && (
            <p className="text-sm text-state-danger mt-3" role="alert">
              {errorLey}
            </p>
          )}
        </Modal>
      )}

      {/* Sprint 18 P3 B3: feedback post-cierre con counters. */}
      {cierreOk && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-md w-full space-y-4">
            <h2 className="text-lg font-medium text-text-primary">
              Cierre completado
            </h2>
            <p className="text-sm text-text-secondary">
              Tu vínculo con la casa se cerró. Resumen de cambios:
            </p>
            <ul className="text-sm text-text-secondary space-y-1 list-disc list-inside">
              <li>{cierreOk.residentes} residente(s) inactivado(s).</li>
              <li>{cierreOk.vehiculos} vehículo(s) dado(s) de baja.</li>
              <li>
                {cierreOk.recursos} recurso(s) físico(s) quedan en la
                bolsa de la casa.
              </li>
              <li>
                {cierreOk.solicitudes} solicitud(es) de recurso
                cancelada(s).
              </li>
            </ul>
            <p className="text-xs text-text-muted">
              La Comisión recibió aviso para bajar accesos físicos. Si
              necesitas algo más, contáctalos.
            </p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setCierreOk(null)}
                className="px-4 py-2 text-sm bg-orange hover:bg-orange/80 text-white font-medium rounded-sm"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sprint 18 P6: modal confirmación eliminar vehículo. */}
      {/* Designar autorizado: el motivo es obligatorio. Escribirlo obliga a
          darse cuenta de lo que se está haciendo — un clic por error no
          sobrevive a tener que explicarlo. */}
      {designando && (
        <ModalConfirmar
          title="Designar autorizado de portal"
          mensaje={
            `Vas a proponer a ${designando.persona_nombre_completo} para ` +
            'administrar tu casa en el portal: podrá agregar residentes y ' +
            'vehículos, pedir accesos y ver el estado de cuenta. ' +
            'La Comisión tiene que aprobarlo antes de que reciba su cuenta. ' +
            'Cuéntanos por qué lo designas.'
          }
          pedirMotivo
          motivoPlaceholder="Por ejemplo: vive conmigo y me ayuda con los pagos"
          textoConfirmar="Enviar a la Comisión"
          error={errorDesignacion}
          onConfirmar={confirmarDesignacion}
          onCancelar={() => {
            setDesignando(null);
            setErrorDesignacion(null);
          }}
        />
      )}

      {vehiculoAEliminar && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-md w-full space-y-4">
            <div className="flex items-start justify-between">
              <h2 className="text-lg font-medium text-text-primary">
                Eliminar vehículo
              </h2>
              <button
                type="button"
                onClick={cerrarModalEliminarVehiculo}
                disabled={eliminandoVehiculo}
                className="text-text-muted hover:text-text-primary text-xl leading-none disabled:opacity-50"
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <p className="text-sm text-text-secondary">
              Vas a eliminar el vehículo con patente{' '}
              <span className="text-text-primary font-medium">
                {vehiculoAEliminar.patente}
              </span>
              . Si tiene control remoto entregado, quedará en la bolsa
              de la casa. Esta acción no es reversible.
            </p>
            {errorEliminarVehiculo && (
              <div
                className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
                role="alert"
              >
                {errorEliminarVehiculo}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={cerrarModalEliminarVehiculo}
                disabled={eliminandoVehiculo}
                className="px-4 py-2 text-sm text-text-secondary hover:bg-bg-sunken rounded-sm disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmarEliminarVehiculo}
                disabled={eliminandoVehiculo}
                className="px-4 py-2 text-sm bg-state-danger hover:bg-state-danger/80 text-white font-medium rounded-sm disabled:opacity-50"
              >
                {eliminandoVehiculo ? 'Eliminando…' : 'Sí, eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Container: orquesta fetch + handlers reales contra el backend.
// Sprint 14 S14-11.
// ============================================================

export function MisDatos() {
  const { me, refreshMe } = useAuth();
  const tenencias = me?.tenencias_activas ?? [];

  const [loteIdSeleccionado, setLoteIdSeleccionado] = useState<string>(
    tenencias[0]?.lote_id ?? '',
  );
  const [residentes, setResidentes] = useState<ResidenteConPersonaRead[]>([]);
  const [vehiculos, setVehiculos] = useState<VehiculoRead[]>([]);
  const [recursos, setRecursos] = useState<RecursoConSuspensionRead[]>([]);
  const [accesosCitofonia, setAccesosCitofonia] = useState<
    AccesoCitofoniaRead[]
  >([]);
  const [accesosWifi, setAccesosWifi] = useState<AccesoWifiRead[]>([]);
  const [resumenArrendado, setResumenArrendado] =
    useState<ResumenArrendado | null>(null);
  const [loadingLote, setLoadingLote] = useState(true);
  const [errorLote, setErrorLote] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!loteIdSeleccionado) {
      setLoadingLote(false);
      return;
    }
    let cancelado = false;
    setLoadingLote(true);
    setErrorLote(null);
    // Sprint 18 P23: si la tenencia del lote es propietario_no_residente,
    // NO se traen los datos del hogar (son del arrendatario) — solo el
    // resumen con cantidades.
    const tenenciaSel = tenencias.find(
      (t) => t.lote_id === loteIdSeleccionado,
    );
    const esNoResidente = tenenciaSel?.tipo === 'propietario_no_residente';
    if (esNoResidente) {
      getResumenArrendado(loteIdSeleccionado)
        .then((r) => {
          if (cancelado) return;
          setResumenArrendado(r);
          setResidentes([]);
          setVehiculos([]);
          setRecursos([]);
          setAccesosCitofonia([]);
          setAccesosWifi([]);
        })
        .catch((err) => {
          if (cancelado) return;
          setErrorLote(
            err instanceof ApiError
              ? `Error ${err.status} al cargar el resumen de la casa`
              : 'Error de red',
          );
        })
        .finally(() => {
          if (!cancelado) setLoadingLote(false);
        });
      return () => {
        cancelado = true;
      };
    }
    setResumenArrendado(null);
    Promise.all([
      listarResidentesDeLote(loteIdSeleccionado),
      listarVehiculosDeLote(loteIdSeleccionado),
      listarRecursosDeLote(loteIdSeleccionado),
      listarCitofoniaDeLote(loteIdSeleccionado),
      listarWifiDeLote(loteIdSeleccionado),
    ])
      .then(([rs, vs, rcs, cito, wifi]) => {
        if (cancelado) return;
        setResidentes(rs);
        setVehiculos(vs);
        setRecursos(rcs);
        setAccesosCitofonia(cito);
        setAccesosWifi(wifi);
      })
      .catch((err) => {
        if (cancelado) return;
        setErrorLote(
          err instanceof ApiError
            ? `Error ${err.status} al cargar datos de tu casa`
            : 'Error de red',
        );
      })
      .finally(() => {
        if (!cancelado) setLoadingLote(false);
      });
    return () => {
      cancelado = true;
    };
  }, [loteIdSeleccionado, refreshKey]);

  if (!me) return null;

  async function onGuardarPersona(
    email: string,
    celular: string,
  ): Promise<string | null> {
    try {
      await autoeditarPersona({
        email: email || null,
        celular: celular || null,
      });
      // Refresca el AuthContext para que los campos actualizados se reflejen
      // en el resto de la app (header, dashboard, etc.).
      if (refreshMe) await refreshMe();
      return null;
    } catch (err) {
      if (err instanceof ApiError) {
        return typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
          ? String((err.body as { detail: unknown }).detail)
          : `Error ${err.status}`;
      }
      return 'Error de red';
    }
  }

  async function onInactivar(
    r: ResidenteConPersonaRead,
    hasta: string,
  ): Promise<string | null> {
    try {
      await inactivarResidente(r.id, hasta);
      setRefreshKey((k) => k + 1);
      return null;
    } catch (err) {
      return err instanceof ApiError
        ? `Error ${err.status}: ${JSON.stringify(err.body)}`
        : 'Error de red';
    }
  }

  async function onEliminarVehiculoApi(v: VehiculoRead): Promise<string | null> {
    try {
      await darDeBajaVehiculo(v.id);
      setRefreshKey((k) => k + 1);
      return null;
    } catch (err) {
      return err instanceof ApiError
        ? `Error ${err.status}: ${JSON.stringify(err.body)}`
        : 'Error de red';
    }
  }

  function mensajeError(err: unknown): string {
    if (err instanceof ApiError) {
      return typeof err.body === 'object' &&
        err.body !== null &&
        'detail' in err.body
        ? String((err.body as { detail: unknown }).detail)
        : `Error ${err.status}`;
    }
    return 'Error de red';
  }

  // Sprint 18 P5 B2 — cambia el slot de citofonía: orquesta baja del
  // acceso anterior (si lo hay y cambia) + solicitud del nuevo slot.
  async function onCambiarCitofoniaApi(
    r: ResidenteConPersonaRead,
    accesoVivo: AccesoCitofoniaRead | null,
    slotNuevo: number | null,
  ): Promise<string | null> {
    if (accesoVivo && accesoVivo.slot === slotNuevo) return null;
    try {
      if (accesoVivo) await darDeBajaCitofonia(accesoVivo.id);
      if (slotNuevo !== null)
        await solicitarCitofonia({ residente_id: r.id, slot: slotNuevo });
      setRefreshKey((k) => k + 1);
      return null;
    } catch (err) {
      return mensajeError(err);
    }
  }

  // Sprint 18 P5 B2 — activa/desactiva el wifi del residente.
  async function onCambiarWifiApi(
    r: ResidenteConPersonaRead,
    accesoVivo: AccesoWifiRead | null,
    activar: boolean,
  ): Promise<string | null> {
    try {
      if (activar && !accesoVivo)
        await solicitarWifi({ residente_id: r.id });
      if (!activar && accesoVivo) await darDeBajaWifi(accesoVivo.id);
      setRefreshKey((k) => k + 1);
      return null;
    } catch (err) {
      return mensajeError(err);
    }
  }

  return (
    <MisDatosBody
      me={me}
      loteIdSeleccionado={loteIdSeleccionado}
      onLoteIdChange={setLoteIdSeleccionado}
      residentes={residentes}
      vehiculos={vehiculos}
      recursos={recursos}
      accesosCitofonia={accesosCitofonia}
      accesosWifi={accesosWifi}
      resumenArrendado={resumenArrendado}
      loadingLote={loadingLote}
      errorLote={errorLote}
      onGuardarPersona={onGuardarPersona}
      onInactivarResidente={onInactivar}
      onEliminarVehiculo={onEliminarVehiculoApi}
      onResidenteAgregado={() => setRefreshKey((k) => k + 1)}
      onVehiculoGuardado={() => setRefreshKey((k) => k + 1)}
      onCambiarCitofonia={onCambiarCitofoniaApi}
      onCambiarWifi={onCambiarWifiApi}
      onTenenciaCerrada={() => {
        // Sprint 18 P3 B3: tras cerrar mi tenencia desde el portal,
        // refrescar /auth/me para que tenencias_activas pierda esta.
        refreshMe?.();
        setRefreshKey((k) => k + 1);
      }}
    />
  );
}
